import React from "react";
import { AppRegistry } from "react-native";
import { loadableReady } from "@loadable/component";
import { ApolloProvider } from "@apollo/client";
import ApolloContext from "@tvg/utils/apolloContext";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { attempt, get } from "lodash";
import axios from "axios";
import { TVGThemeProviderWeb, ToastManager } from "@tvg/design-system";
import { TvgSafeAreaProvider } from "@tvg/design-system/src/utils/tvgSafeAreaProvider";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import sendToAppsFlyer from "@tvg/utils/appsflyerUtils";
import "@tvg/gtm";
import "@fdr/gtm";
import { recoverQueriesFromBackground } from "@tvg/utils/generalUtils";
import { T } from "lodash/fp";
import qa from "../../config/qa";
import staging from "../../config/staging";
import production from "../../config/production";

import { configureStore } from "../shared/configureStore";
import ApolloClient from "../shared/apolloClient/ApolloClient";
import App from "../shared/pages";

// gets conf for specific host / device
const tvg = tvgConf(window.location.hostname, window.navigator.userAgent);

// Gets context name
const getContext = () => {
  const { product } = tvg;
  if (product.includes("fdrios")) {
    return "ios";
  }
  if (product.includes("fdrandroid")) {
    return "android";
  }

  return "browser";
};

// gets server side generated initial redux store
const store =
  window.store ||
  configureStore({
    initialState: window.__PRELOADED_STATE__ // eslint-disable-line
  });

// register service worker
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker // eslint-disable-line
      .register("/service-worker.js")
      .then((registration) =>
        console.log(
          "ServiceWorker registration successful with scope: ",
          registration.scope
        )
      )
      .catch((err) => {
        console.log("ServiceWorker registration failed: ", err);
      });
  });
}

// Event to catch when the user installs the PWA
window.addEventListener("beforeinstallprompt", (e) => {
  e.userChoice.then((choiceResult) => {
    if (choiceResult.outcome === "dismissed") {
      mediator.base.dispatch({
        type: "SITE_CLICK",
        payload: {
          action: "Close",
          label: "Add To Home Screen",
          module: "Add To Home Screen"
        }
      });
    } else {
      mediator.base.dispatch({
        type: "SITE_CLICK",
        payload: {
          action: "Add To Home Screen",
          label: "Add To Home Screen",
          module: "Add To Home Screen"
        }
      });
    }
  });
});

// detect private mode
(() => {
  const detectPrivateMode = (cb) => {
    let db;
    const on = cb.bind(null, true);
    const off = cb.bind(null, false);

    const tryls = () => {
      try {
        window.openDatabase(null, null, null, null);
        off();
      } catch (e) {
        on();
      }
    };

    if (window.webkitRequestFileSystem) {
      window.webkitRequestFileSystem(0, 0, off, on);
      // $FlowFixMe
    } else if ("MozAppearance" in document.documentElement.style) {
      // $FlowFixMe
      db = indexedDB.open("test");
      db.onerror = on;
      db.onsuccess = off;
    } else if (/constructor/i.test(window.HTMLElement) || window.safari) {
      tryls();
    } else if (
      !window.indexedDB &&
      (window.PointerEvent || window.MSPointerEvent)
    ) {
      on();
    } else {
      off();
    }
  };

  detectPrivateMode((isPrivateMode) => {
    if (isPrivateMode) {
      // $FlowBug
      attempt(() => localStorage.setItem("privateMode", true));
    }
  });
})();

window.onerror = (msg, url, line, column, error) => {
  const accountId = attempt(() => sessionStorage.getItem("userId"));
  if (msg && msg !== "Script error.") {
    axios({
      url: "/logerror",
      method: "post",
      withCredentials: true,
      data: {
        msg,
        error: error && error.stack,
        accountId,
        tvgContext: tvg.context()
      }
    });
  }

  return false;
};

if (process.env.NODE_ENV === "development") {
  if (module.hot) {
    module.hot.accept();
  }

  // $FlowBug
  if (!window.store) {
    window.store = store;
  }
}

// Send AppsFlyer app launch event for iOS app
const didAppLaunch = sessionStorage.getItem("didAppLaunch");

if (!didAppLaunch) {
  sendToAppsFlyer({
    key: "af_app_launch"
  });

  sessionStorage.setItem("didAppLaunch", true);
}

const defaultClient = ApolloClient.createClient(false, "graph", true);
const fcpClient = ApolloClient.createClient(false, "fcp");
const rdaClient = ApolloClient.createClient(false, "rda");
const gasClient = ApolloClient.createClient(false, "gas", true);
const behgClient = ApolloClient.createClient(false, "behg", true);

if (typeof window !== "undefined") {
  recoverQueriesFromBackground([
    defaultClient,
    fcpClient,
    rdaClient,
    gasClient,
    behgClient
  ]);
}

const loadPerimeterXScript = get(
  window,
  "__PRELOADED_STATE__.capi.featureToggles.enablePerimeterxFDR",
  false
);
// This script needs to load in client side to be able to load the px script
// This will be placed only 1 time even if user refreshes or navigates to a different route
// The idea is to load the px script in client without changing the tvgConf package
if (loadPerimeterXScript) {
  import("@tvg/perimeterx/perimeterx");
}

const Main = () => (
  <TVGThemeProviderWeb isFanduelTheme={false}>
    <TvgSafeAreaProvider>
      <ApolloProvider client={defaultClient}>
        <ApolloContext.Provider
          value={{
            fcpClient,
            rdaClient,
            gasClient,
            behgClient
          }}
        >
          <Provider store={store}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </Provider>
        </ApolloContext.Provider>
      </ApolloProvider>
      <ToastManager
        qaLabel="toast-manager"
        variant="informational"
        hasRoundedStyle
        message="default message!"
        ref={(ref) => ToastManager.setRef(ref)}
      />
    </TvgSafeAreaProvider>
  </TVGThemeProviderWeb>
);

loadableReady(() => {
  AppRegistry.registerComponent("App", () => Main);

  AppRegistry.runApplication("App", {
    initialProps: {},
    rootTag: document.getElementById("app")
  });
});
